import Person from '@mui/icons-material/Person'
import Search from '@mui/icons-material/Search'
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Typography,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  Tooltip,
  useTheme,
  TextField,
  InputAdornment,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { styled } from '@mui/material/styles'
import {
  getDataMapFromCommonResponse,
  getImageLink,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { toast } from 'react-toastify'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { getSPDataWithParams, getSPDataWithoutParams } from 'src/supportPortal/services/services'
import {
  getSupportPractitionerCommunicationGates,
  getSupportPractitionerPatientContactVisibility,
  getSupportPractitionerPatientScope,
  saveSupportPractitionerCommunicationGates,
  saveSupportPractitionerPatientContactVisibility,
  saveSupportPractitionerPatientScope,
} from 'src/supportPortal/services/practitionerOnboardingService'
import PractitionerActionButtons from 'src/supportPortal/components/common/mentor/PractitionerActionButtons'
import PractitionerStatusChips from 'src/supportPortal/components/common/mentor/PractitionerStatusChips'
import PractitionerEnableDisableControl from 'src/supportPortal/components/common/mentor/PractitionerEnableDisableControl'
import { derivePractitionerEnabled } from 'src/supportPortal/utils/practitionerStatus'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMore from '@mui/icons-material/ExpandMore'
import ProfileDrawer from 'src/supportPortal/components/profile/ProfileDrawer'
import SupportContactActions from 'src/supportPortal/components/contact/SupportContactActions'
import ProfileViewer from 'src/mentor/components/MentorProfilePage/components/ProfileViewer'
import ScreenerProfileViewer from 'src/supportPortal/components/profile/ScreenerProfileViewer'
import ScreenerRoleSourceBadges from 'src/supportPortal/components/common/mentor/ScreenerRoleSourceBadges'
import ScreenerProfileCreateDialog from 'src/supportPortal/components/common/mentor/ScreenerProfileCreateDialog'
import ScreenerClinicAssignmentDialog from 'src/supportPortal/components/common/mentor/ScreenerClinicAssignmentDialog'
import SupportPractitionerStatsDrawer from 'src/supportPortal/components/common/stats/SupportPractitionerStatsDrawer'
import BusinessIcon from '@mui/icons-material/Business'
import useServerAuth from 'src/customHooks/useServerAuth'
import {
  COMMUNICATION_GATE_THERAPY_REPORT_COMPLETION_REMINDER,
  PAYOUT_CYCLE_COMMUNICATION_GATE_ITEMS,
  buildPractitionerCommunicationGateSettings,
  getPractitionerCommunicationGateSettings,
} from 'src/supportPortal/utils/practitionerCommunicationGateSettings'

// Phase 14x — humanise approved-discipline slugs for the chips rendered below practitioner
// names. Falls back to a sentence-cased version of the slug for any new discipline value
// the support / onboarding flows introduce later.
function formatDisciplineLabel(slug) {
  if (!slug) return ''
  const known = {
    psychotherapy: 'Psychotherapy',
    'nutrition-consultation': 'Nutrition Consultation',
  }
  if (known[slug]) return known[slug]
  return slug
    .split('-')
    .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : part))
    .join(' ')
}

const TherapistCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: '1400px',
  borderRadius: '16px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08)',
    transform: 'translateY(-2px)',
  },
  [theme.breakpoints.down('sm')]: {
    borderRadius: '12px',
  },
}))

const ProfileSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  cursor: 'pointer',
  padding: theme.spacing(1),
  borderRadius: '12px',
  transition: 'all 0.2s ease',
  flex: 1,
  minWidth: 0,
  '&:hover': {
    backgroundColor: 'rgba(216, 101, 0, 0.04)',
  },
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1),
    padding: theme.spacing(0.75),
  },
}))

const Container = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(3, 2),
  background: 'linear-gradient(180deg, rgba(249, 250, 251, 0.5) 0%, rgba(243, 244, 246, 0.3) 100%)',
  minHeight: 'calc(100vh - 200px)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2, 1.5),
  },
}))

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(8),
  width: '100%',
}))

const SearchContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '1400px',
  margin: '0 auto',
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2),
  },
}))

const SearchActionsRow = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '1400px',
  margin: '0 auto',
  marginBottom: theme.spacing(3),
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  gap: theme.spacing(1.5),
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
  },
}))

const SearchTextField = styled(TextField)(({ theme }) => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08)',
    },
    '&.Mui-focused': {
      boxShadow: '0 4px 16px rgba(216, 101, 0, 0.15), 0 2px 6px rgba(216, 101, 0, 0.1)',
      borderColor: 'rgba(216, 101, 0, 0.3)',
    },
    '& fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.1)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(216, 101, 0, 0.2)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'rgba(216, 101, 0, 0.4)',
    },
  },
  '& .MuiInputBase-input': {
    padding: theme.spacing(1.5, 1.5, 1.5, 0),
    fontSize: '0.9375rem',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.25, 1.25, 1.25, 0),
      fontSize: '0.875rem',
    },
  },
}))

const EMPTY_LIST_API_PARAMS = {}

// All-time cumulative totals shown on each listing card (practitioner-stats admin surface).
// Report-type entries hide when zero (a doctor's card doesn't show "0 Therapy Reports");
// Patients always shows once stats have loaded so "0 Patients" reads as "no reports yet".
const CUMULATIVE_STAT_DEFS = [
  { key: 'prescriptions', label: 'Consultations', hideWhenZero: true },
  { key: 'therapyReports', label: 'Therapy Reports', hideWhenZero: true },
  { key: 'screeningReports', label: 'Screenings', hideWhenZero: true },
  { key: 'patients', label: 'Patients', hideWhenZero: false },
]

function CumulativeStatsStrip({ stats }) {
  if (!stats) return null
  const entries = CUMULATIVE_STAT_DEFS.filter(
    (def) => !def.hideWhenZero || Number(stats[def.key]) > 0,
  )
  return (
    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
      {entries.map((def) => (
        <Chip
          key={def.key}
          label={`${Number(stats[def.key] || 0).toLocaleString('en-IN')} ${def.label}`}
          size="small"
          sx={{
            height: '20px',
            fontSize: '0.6875rem',
            backgroundColor: 'rgba(65, 133, 113, 0.1)',
            color: 'rgba(65, 133, 113, 0.95)',
            fontWeight: 600,
          }}
        />
      ))}
    </Stack>
  )
}

export default function SupportMentorsListing({
  heading = 'Therapists',
  hideHeading = false,
  mentorsListApiSignature = '/api/secure/cc/support/therapists/clinical/list',
  listApiParams = EMPTY_LIST_API_PARAMS,
  variant = 'therapist',
  exculudeMenuItemsList = [],
  screenerNoSpecialtyFilter = false,
  showOnboardingButton = false,
  onboardingPractitionerType = '',
  // Phase 14x — optional client-side filter on TeacherUnEditableFields.approvedTherapyDisciplines.
  // Pass a single slug like 'psychotherapy' or 'nutrition-consultation' to narrow the list down
  // to practitioners with that discipline approved. null / empty disables the filter.
  disciplineFilter = null,
  // Phase 14x — show the practitioner's approved therapy disciplines as small chips below the
  // name. Off by default to keep existing surfaces unchanged; the therapists listing turns
  // it on so the discipline-aware tabs make sense visually.
  showDisciplineChips = false,
  // "All Screeners (by role)" tab — render per-row ROLE_SCREENER source + Screener Profile
  // presence badges (reads the flags the /screeners/role-holders/list endpoint attaches), so
  // gaps in either role source are highlighted.
  showScreenerRoleSourceBadges = false,
  // "All Screeners (by role)" tab — render a Create / Edit Screener Profile button per row.
  // Gated to ROLE_SCREENER_ADMIN here AND server-side; only enabled once the practitioner has
  // servicesApproved.ROLE_SCREENER turned on (the save endpoint requires it).
  enableScreenerProfileCreation = false,
}) {
  const navigate = useNavigate()
  const { allRolesInBoolean } = useServerAuth()
  const [therapistList, setAllTherapists] = useState()
  const [loadingTherapists, setLoadingTherapists] = useState()
  const [entityFeeModels, setEntityFeeModels] = useState()
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false)
  const [selectedMentor, setSelectedMentor] = useState(null)
  const [fullProfileData, setFullProfileData] = useState(null)
  const [screenerProfileData, setScreenerProfileData] = useState(null)
  const [allTags, setAllTags] = useState(null)
  const [loadingProfile, setLoadingProfile] = useState(false)
  const [patientContactVisibility, setPatientContactVisibility] = useState(null)
  const [loadingPatientContactVisibility, setLoadingPatientContactVisibility] = useState(false)
  const [savingPatientContactVisibility, setSavingPatientContactVisibility] = useState(false)
  const [patientScope, setPatientScope] = useState(null)
  const [loadingPatientScope, setLoadingPatientScope] = useState(false)
  const [savingPatientScope, setSavingPatientScope] = useState(false)
  const [communicationGateSettings, setCommunicationGateSettings] = useState(null)
  const [loadingCommunicationGates, setLoadingCommunicationGates] = useState(false)
  const [savingCommunicationGateKey, setSavingCommunicationGateKey] = useState('')
  const [communicationGatesError, setCommunicationGatesError] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  // "All Screeners (by role)" — the row whose Screener Profile is being created/edited (null = closed).
  const [screenerProfileDialogMentor, setScreenerProfileDialogMentor] = useState(null)
  // Screener whose clinic-location assignments are being viewed/edited (null = closed).
  const [clinicAssignmentMentor, setClinicAssignmentMentor] = useState(null)

  // Per-card cumulative stats (all-time report + distinct-patient totals), served by the
  // practitioner-stats admin endpoint — so only fetched/rendered for roles that endpoint admits.
  const canViewCumulativeStats = Boolean(
    allRolesInBoolean?.isPractitionerStatsAdmin || allRolesInBoolean?.isAdmin,
  )
  const [cumulativeStatsById, setCumulativeStatsById] = useState({})
  // Practitioner whose across-all-clinics stats drawer is open (null = closed).
  const [statsDrawerMentor, setStatsDrawerMentor] = useState(null)

  const isScreenerAdmin = Boolean(allRolesInBoolean?.isScreenerAdmin)
  const canManageScreenerProfiles = enableScreenerProfileCreation && isScreenerAdmin
  // Clinic assignments are loaded from /clinics-with-brands (clinic roles only) and edited by
  // CSA / CSM. Gate the button to those roles so the dialog always has a tree to render.
  const canSeeClinicAssignments = Boolean(
    allRolesInBoolean?.isClinicSuccessAdmin ||
      allRolesInBoolean?.isClinicSuccessManager ||
      allRolesInBoolean?.isAdmin,
  )
  const isScreener = variant === 'screener'
  // Determine base path based on current location or API signature
  const isDoctors = mentorsListApiSignature.includes('/doctor/')
  const searchPlaceholder = isDoctors
    ? 'Search doctors by name...'
    : isScreener
      ? 'Search screeners by name...'
      : 'Search therapists by name...'
  const selectedMentorTargetRole = isScreener ? 'screener' : isDoctors ? 'doctor' : 'therapist'
  const selectedMentorEventType = isScreener
    ? 'screening'
    : isDoctors
    ? 'consultation'
    : 'therapy-session'
  const selectedPractitionerLabel = isScreener ? 'Screener' : isDoctors ? 'Doctor' : 'Therapist'
  const resolvedOnboardingType = onboardingPractitionerType || selectedMentorTargetRole
  const canSeeOnboardingButton =
    showOnboardingButton &&
    (allRolesInBoolean.isAdmin ||
      ((resolvedOnboardingType === 'doctor' &&
        allRolesInBoolean.isClinicSuccessAdmin) ||
        ((resolvedOnboardingType === 'therapist' || resolvedOnboardingType === 'screener') &&
          allRolesInBoolean.isTherapistAdmin)))
  const onboardingButtonLabel =
    resolvedOnboardingType === 'doctor'
      ? 'Add New Doctor'
      : resolvedOnboardingType === 'screener'
        ? 'Add New Screener'
        : 'Add New Therapist'

  // The selected practitioner for the profile drawer — falls back to the listing row data
  // until the fuller profile record loads.
  const selectedPractitionerForStatus = fullProfileData
    ? { ...selectedMentor, ...fullProfileData }
    : selectedMentor

  // Edit-profile gate. Mirrors the support practitioner-onboarding controller's
  // @PreAuthorize (ROLE_ADMIN / ROLE_THERAPIST_ADMIN / ROLE_CLINIC_SUCCESS_ADMIN) so the
  // button only renders for actors who can actually save changes — and the save endpoint
  // is what writes the audit-log entry, not the open action.
  const canEditPractitionerProfile = Boolean(
    selectedMentor?.id &&
      (allRolesInBoolean.isAdmin ||
        (selectedMentorTargetRole === 'doctor'
          ? allRolesInBoolean.isClinicSuccessAdmin
          : allRolesInBoolean.isTherapistAdmin)),
  )
  const canManagePatientContactVisibilityForListing =
    allRolesInBoolean.isAdmin ||
    (selectedMentorTargetRole === 'doctor'
      ? allRolesInBoolean.isClinicAdmin
      : allRolesInBoolean.isTherapistAdmin)
  const canManagePatientContactVisibility = Boolean(
    selectedMentor?.id && canManagePatientContactVisibilityForListing,
  )
  const showPatientContactDetailsOnMentorPortal =
    patientContactVisibility?.showPatientContactDetailsOnMentorPortal !== false
  // Patient scope (own patients vs all clinic patients) uses the same role model as contact visibility.
  const canManagePatientScopeForListing = canManagePatientContactVisibilityForListing
  const canManagePatientScope = Boolean(selectedMentor?.id && canManagePatientScopeForListing)
  const restrictedToOwnPatients = patientScope?.canOnlyViewTheirOwnPatients === true
  const canManageTherapyReportReminderForListing =
    selectedMentorTargetRole === 'therapist' &&
    (allRolesInBoolean.isAdmin || allRolesInBoolean.isTherapistAdmin)
  const canManagePayoutCycleCommunicationForListing =
    selectedMentorTargetRole === 'therapist' &&
    (allRolesInBoolean.isAdmin || allRolesInBoolean.isTherapistAdmin)
  const canManageTherapyReportReminder = Boolean(
    selectedMentor?.id && canManageTherapyReportReminderForListing,
  )
  const canManagePayoutCycleCommunication = Boolean(
    selectedMentor?.id && canManagePayoutCycleCommunicationForListing,
  )
  const canManageCommunicationGatesForListing =
    canManageTherapyReportReminderForListing || canManagePayoutCycleCommunicationForListing
  const communicationGateValues =
    communicationGateSettings || getPractitionerCommunicationGateSettings(fullProfileData || selectedMentor)
  const therapyReportReminderEnabled = Boolean(
    communicationGateValues[COMMUNICATION_GATE_THERAPY_REPORT_COMPLETION_REMINDER],
  )

  const handleEditPractitionerProfile = () => {
    if (!selectedMentor?.id) return
    setProfileDrawerOpen(false)
    navigate(
      `/support-home/practitioner-management/onboarding?type=${encodeURIComponent(
        selectedMentorTargetRole,
      )}&practitioner-id=${encodeURIComponent(selectedMentor.id)}`,
    )
  }

  const allTherapists = useMemo(() => {
    if (therapistList) {
      let list = therapistList

      // Screeners – No specialty: filter to mentors with no target speciality
      if (screenerNoSpecialtyFilter) {
        list = list.filter((m) => {
          const slugs = m?.teacherUnEditableFields?.servicesApproved?.ROLE_SCREENER?.targetSpecialitySlugs
          return !slugs || (Array.isArray(slugs) && slugs.length === 0)
        })
      }

      // Phase 14x — discipline filter. The disciplines live on
      // TeacherUnEditableFields.approvedTherapyDisciplines (already returned by the
      // /list endpoint, see MinimalTeacherResponse). Match defensively in case some
      // legacy practitioners are missing the field — they fall through to "no
      // discipline" and are excluded from a discipline-scoped tab.
      if (disciplineFilter) {
        list = list.filter((m) => {
          const disciplines = m?.teacherUnEditableFields?.approvedTherapyDisciplines
          return Array.isArray(disciplines) && disciplines.includes(disciplineFilter)
        })
      }

      const therapistsWithFeeModels = list.map((therapist) => {
        const foundEntityFeeModels = entityFeeModels?.filter((model) => {
          return model?.entityId === therapist?.id
        })
        return {
          ...therapist,
          entityFeeModels: foundEntityFeeModels || [],
        }
      })

      // Filter by search query
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim()
        return therapistsWithFeeModels.filter((therapist) =>
          therapist?.name?.toLowerCase().includes(query)
        )
      }

      return therapistsWithFeeModels
    }
    return []
  }, [therapistList, entityFeeModels, searchQuery, screenerNoSpecialtyFilter, disciplineFilter])

  async function getTherapistsList() {
    // includeDisabled keeps paused practitioners in the listing so they render greyed-out in
    // place (with a Disabled status chip) instead of disappearing onto a separate page.
    const resp = await getSPDataWithParams(mentorsListApiSignature, {
      ...listApiParams,
      includeDisabled: true,
    })
    if (resp.status === 200) {
      if (isCommonResponseSuccessfulV2(resp)) {
        const mentorsList = getDataMapFromCommonResponse(resp)?.mentor
        setAllTherapists(mentorsList)
      }
    }
  }

  async function getEntityFeeModelsForMentors() {
    const resp = await getSPDataWithParams('/api/secure/cc/support/entity-fee-model/list', {
      'practitioner-ids': (therapistList?.map((therapist) => therapist?.id) || []).join(','),
    })
    if (resp.status === 200) {
      if (isCommonResponseSuccessfulV2(resp)) {
        const entityFeeModels = getDataMapFromCommonResponse(resp)?.['entity-fee-model']
        setEntityFeeModels(entityFeeModels)
      }
    }
  }

  // The backend caps mentorIds per cumulative call at 200 — chunk the listing accordingly.
  async function getCumulativeStatsForMentors() {
    const ids = (therapistList || []).map((therapist) => therapist?.id).filter(Boolean)
    if (!ids.length) return
    const CHUNK_SIZE = 200
    const merged = {}
    for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
      const resp = await getSPDataWithParams('/api/secure/cc/support/practitioner-stats/cumulative', {
        mentorIds: ids.slice(i, i + CHUNK_SIZE).join(','),
      })
      if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
        Object.assign(
          merged,
          getDataMapFromCommonResponse(resp)?.practitionerCumulativeStats || {},
        )
      }
    }
    setCumulativeStatsById((prev) => ({ ...prev, ...merged }))
  }

  async function setup() {
    setLoadingTherapists(true)
    await getTherapistsList()
    setLoadingTherapists(false)
  }

  async function entityFeeModelsSetup() {
    setLoadingTherapists(true)
    if (therapistList && !!therapistList?.length) {
      await getEntityFeeModelsForMentors()
    }
    setLoadingTherapists(false)
  }

  // Fetch full profile data and allTags when opening profile drawer
  const fetchFullProfileData = async (mentorId) => {
    setLoadingProfile(true)
    setFullProfileData(null)
    setAllTags(null)
    
    try {
      const profileResp = await getSPDataWithoutParams(
        `/api/secure/cc/support/practitioner/profile?practitioner-id=${mentorId}`,
      )
      if (profileResp?.status === 200) {
        // Handle different response structures
        const profileData =
          profileResp.data?.payload ||
          profileResp.data?.data ||
          profileResp.data ||
          null
        if (profileData) {
          setFullProfileData(profileData)
        }
      }
      
      // Fetch allTags (domains) for category display
      const tagsResp = await getSPDataWithoutParams('/api/secure/cc/support/domain/all/list')
      if (tagsResp?.status === 200) {
        const tagsData = tagsResp.data?.payload || tagsResp.data?.data || tagsResp.data || null
        if (tagsData) {
          setAllTags(Array.isArray(tagsData) ? tagsData : tagsData?.domains || [])
        }
      }
    } catch (error) {
      console.error('Error fetching profile data:', error)
      // On error, still show the basic mentor data from listing
    } finally {
      setLoadingProfile(false)
    }
  }

  const fetchScreenerProfile = async (mentorId) => {
    setScreenerProfileData(null)
    try {
      const resp = await getSPDataWithParams('/api/secure/cc/support/screeners/profiles/by-mentor-id', {
        mentorId,
        includeDisabled: true,
        includeDeleted: false,
      })
      if (resp?.status === 200) {
        const raw = getDataMapFromCommonResponse(resp) || resp?.data?.data || resp?.data
        const data = raw?.screenerProfile ?? raw?.profile ?? raw
        setScreenerProfileData(data && typeof data === 'object' ? data : null)
      }
    } catch (e) {
      console.error('Error fetching screener profile:', e)
    }
  }

  const fetchPatientContactVisibility = async (mentor) => {
    if (!mentor?.id) return
    setLoadingPatientContactVisibility(true)
    setPatientContactVisibility(null)
    try {
      const response = await getSupportPractitionerPatientContactVisibility({
        practitionerId: mentor.id,
        practitionerSlug: mentor.slug,
      })
      if (!response.ok) {
        toast.error(response.message || 'Unable to load patient contact visibility.')
        return
      }
      setPatientContactVisibility(response.data)
    } catch (error) {
      console.error('Error fetching patient contact visibility:', error)
      toast.error('Unable to load patient contact visibility.')
    } finally {
      setLoadingPatientContactVisibility(false)
    }
  }

  const fetchPatientScope = async (mentor) => {
    if (!mentor?.id) return
    setLoadingPatientScope(true)
    setPatientScope(null)
    try {
      const response = await getSupportPractitionerPatientScope({
        practitionerId: mentor.id,
        practitionerSlug: mentor.slug,
      })
      if (!response.ok) {
        toast.error(response.message || 'Unable to load patient scope.')
        return
      }
      setPatientScope(response.data)
    } catch (error) {
      console.error('Error fetching patient scope:', error)
      toast.error('Unable to load patient scope.')
    } finally {
      setLoadingPatientScope(false)
    }
  }

  const fetchPractitionerCommunicationGates = async (mentor) => {
    if (!mentor?.id) return
    setLoadingCommunicationGates(true)
    setCommunicationGateSettings(null)
    setCommunicationGatesError('')
    try {
      const response = await getSupportPractitionerCommunicationGates({
        practitionerId: mentor.id,
        practitionerSlug: mentor.slug,
      })
      if (!response.ok) {
        const message = response.message || 'Unable to load practitioner communication gates.'
        setCommunicationGatesError(message)
        toast.error(message)
        return
      }
      setCommunicationGateSettings(
        getPractitionerCommunicationGateSettings(response.data || {}),
      )
    } catch (error) {
      console.error('Error fetching practitioner communication gates:', error)
      setCommunicationGatesError('Unable to load practitioner communication gates.')
      toast.error('Unable to load practitioner communication gates.')
    } finally {
      setLoadingCommunicationGates(false)
    }
  }

  const handleProfileClick = async (mentor) => {
    setSelectedMentor(mentor)
    setProfileDrawerOpen(true)
    setScreenerProfileData(null)
    setPatientContactVisibility(null)
    setPatientScope(null)
    setCommunicationGateSettings(null)
    setCommunicationGatesError('')
    if (mentor?.id) {
      const fetchTasks = []
      if (isScreener) {
        fetchTasks.push(fetchScreenerProfile(mentor.id), fetchFullProfileData(mentor.id))
      } else {
        fetchTasks.push(fetchFullProfileData(mentor.id))
      }
      if (canManagePatientContactVisibilityForListing) {
        fetchTasks.push(fetchPatientContactVisibility(mentor))
      }
      if (canManagePatientScopeForListing) {
        fetchTasks.push(fetchPatientScope(mentor))
      }
      if (canManageCommunicationGatesForListing) {
        fetchTasks.push(fetchPractitionerCommunicationGates(mentor))
      }
      await Promise.all(fetchTasks)
    }
  }

  const handleProfileDrawerClose = () => {
    setProfileDrawerOpen(false)
    setSelectedMentor(null)
    setFullProfileData(null)
    setScreenerProfileData(null)
    setPatientContactVisibility(null)
    setPatientScope(null)
    setCommunicationGateSettings(null)
    setCommunicationGatesError('')
    setAllTags(null)
  }

  // Called by PractitionerEnableDisableControl after a successful enable/disable — patches the
  // drawer + listing state in place so the status chips and greyed-out row update immediately.
  const handlePractitionerStatusUpdated = (refreshedPractitioner, nextEnabled) => {
    if (!selectedMentor?.id) {
      return
    }
    const patch = (item) =>
      item
        ? {
            ...item,
            ...refreshedPractitioner,
            enabled: nextEnabled,
            services: {
              ...(item?.services || {}),
              ...(refreshedPractitioner?.services || {}),
              live: nextEnabled,
            },
          }
        : item
    setSelectedMentor((prev) => patch(prev))
    setFullProfileData((prev) => (prev ? patch(prev) : prev))
    setAllTherapists((prev) =>
      Array.isArray(prev)
        ? prev.map((item) => (item?.id === selectedMentor.id ? patch(item) : item))
        : prev,
    )
  }

  // After a Screener Profile is created/edited from the role-holders tab, patch the row in place so
  // the badge flips to "Screener Profile ✓" and the button becomes "Edit" without a full refetch.
  const handleScreenerProfileSaved = (savedProfile) => {
    const mentorId = screenerProfileDialogMentor?.id
    if (!mentorId) return
    const patch = (item) =>
      item?.id === mentorId
        ? {
            ...item,
            hasScreenerProfile: true,
            screenerProfileEnabled: savedProfile?.enabled !== false,
          }
        : item
    setAllTherapists((prev) => (Array.isArray(prev) ? prev.map(patch) : prev))
  }

  const handlePatientContactVisibilityChange = async (event) => {
    if (!selectedMentor?.id) return
    const nextVisible = event.target.checked
    const previousVisibility = patientContactVisibility

    setSavingPatientContactVisibility(true)
    setPatientContactVisibility((prev) => ({
      ...(prev || {}),
      practitionerId: selectedMentor.id,
      practitionerSlug: selectedMentor.slug,
      practitionerType: selectedMentorTargetRole,
      showPatientContactDetailsOnMentorPortal: nextVisible,
    }))

    try {
      const response = await saveSupportPractitionerPatientContactVisibility(
        {
          practitionerId: selectedMentor.id,
          practitionerSlug: selectedMentor.slug,
        },
        {
          showPatientContactDetailsOnMentorPortal: nextVisible,
        },
      )

      if (!response.ok) {
        setPatientContactVisibility(previousVisibility)
        toast.error(response.message || 'Unable to save patient contact visibility.')
        return
      }

      setPatientContactVisibility(response.data)
      toast.success(
        nextVisible
          ? `Patient contact details enabled for this ${selectedPractitionerLabel.toLowerCase()}.`
          : `Patient contact details hidden for this ${selectedPractitionerLabel.toLowerCase()}.`,
      )
    } catch (error) {
      console.error('Error saving patient contact visibility:', error)
      setPatientContactVisibility(previousVisibility)
      toast.error('Unable to save patient contact visibility.')
    } finally {
      setSavingPatientContactVisibility(false)
    }
  }

  const handlePatientScopeChange = async (event) => {
    if (!selectedMentor?.id) return
    const nextRestricted = event.target.checked
    const previousScope = patientScope

    setSavingPatientScope(true)
    setPatientScope((prev) => ({
      ...(prev || {}),
      practitionerId: selectedMentor.id,
      practitionerSlug: selectedMentor.slug,
      practitionerType: selectedMentorTargetRole,
      canOnlyViewTheirOwnPatients: nextRestricted,
    }))

    try {
      const response = await saveSupportPractitionerPatientScope(
        {
          practitionerId: selectedMentor.id,
          practitionerSlug: selectedMentor.slug,
        },
        {
          canOnlyViewTheirOwnPatients: nextRestricted,
        },
      )

      if (!response.ok) {
        setPatientScope(previousScope)
        toast.error(response.message || 'Unable to save patient scope.')
        return
      }

      setPatientScope(response.data)
      toast.success(
        nextRestricted
          ? `This ${selectedPractitionerLabel.toLowerCase()} can now see only their own patients.`
          : `This ${selectedPractitionerLabel.toLowerCase()} can now see all clinic patients.`,
      )
    } catch (error) {
      console.error('Error saving patient scope:', error)
      setPatientScope(previousScope)
      toast.error('Unable to save patient scope.')
    } finally {
      setSavingPatientScope(false)
    }
  }

  const handleCommunicationGateChange = async (gateKey, enabled, successLabels = {}) => {
    if (!selectedMentor?.id) return

    const previousSettings = communicationGateSettings
    const nextSettings = buildPractitionerCommunicationGateSettings(
      communicationGateValues,
      gateKey,
      enabled,
    )

    setSavingCommunicationGateKey(gateKey)
    setCommunicationGateSettings(nextSettings)

    try {
      const response = await saveSupportPractitionerCommunicationGates(
        {
          practitionerId: selectedMentor.id,
          practitionerSlug: selectedMentor.slug,
        },
        nextSettings,
      )

      if (!response.ok) {
        setCommunicationGateSettings(previousSettings)
        toast.error(response.message || 'Unable to save communication setting.')
        return
      }

      setCommunicationGateSettings(
        getPractitionerCommunicationGateSettings(response.data || { communicationGateSettings: nextSettings }),
      )
      toast.success(enabled ? successLabels.enabled : successLabels.disabled)
    } catch (error) {
      console.error('Error saving practitioner communication gate:', error)
      setCommunicationGateSettings(previousSettings)
      toast.error('Unable to save communication setting.')
    } finally {
      setSavingCommunicationGateKey('')
    }
  }

  const handleTherapyReportReminderChange = (event) => {
    handleCommunicationGateChange(
      COMMUNICATION_GATE_THERAPY_REPORT_COMPLETION_REMINDER,
      event.target.checked,
      {
        enabled: 'Therapy report completion reminders enabled.',
        disabled: 'Therapy report completion reminders disabled.',
      },
    )
  }

  const theme = useTheme()

  useEffect(() => {
    entityFeeModelsSetup()
  }, [therapistList])

  useEffect(() => {
    if (canViewCumulativeStats) {
      getCumulativeStatsForMentors()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [therapistList, canViewCumulativeStats])

  useEffect(() => {
    setup()
  }, [mentorsListApiSignature, listApiParams])

  return (
    <>
      {!hideHeading && <SupportHeader heading={heading} showBackButton />}
      <Container>
        <SearchActionsRow>
          <SearchContainer sx={{ marginBottom: 0 }}>
          <SearchTextField
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'rgba(216, 101, 0, 0.6)' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiInputBase-root': {
                fontSize: { xs: '0.875rem', sm: '0.9375rem' },
              },
            }}
          />
          </SearchContainer>
          {canSeeOnboardingButton && (
            <Button
              variant="contained"
              startIcon={<PersonAddAlt1Icon />}
              onClick={() =>
                navigate(
                  `/support-home/practitioner-management/onboarding?type=${encodeURIComponent(
                    resolvedOnboardingType,
                  )}`,
                )
              }
              sx={{
                minHeight: 52,
                borderRadius: 3,
                px: 2.5,
                whiteSpace: 'nowrap',
              }}
            >
              {onboardingButtonLabel}
            </Button>
          )}
        </SearchActionsRow>
        {loadingTherapists ? (
          <LoadingContainer>
            <CircularProgress size={48} />
          </LoadingContainer>
        ) : (
          <Stack
            spacing={2}
            sx={{
              width: '100%',
              maxWidth: '1400px',
              margin: '0 auto',
            }}
          >
            {allTherapists
              ?.sort((a, b) => a.name?.localeCompare(b.name))
              ?.map((ther) => {
                // Disabled practitioners stay in the listing but are visually de-emphasised —
                // dimmed, with a warning-toned left accent — so they read as paused at a glance.
                const rowEnabled = derivePractitionerEnabled(ther)
                return (
                <TherapistCard
                  key={ther?.id || ther?.userId || ther?.slug}
                  sx={
                    rowEnabled
                      ? undefined
                      : {
                          opacity: 0.78,
                          borderLeft: '4px solid',
                          borderLeftColor: 'warning.main',
                          background: 'rgba(245, 158, 11, 0.05)',
                        }
                  }
                >
                  <CardContent
                    sx={{
                      padding: { xs: theme.spacing(2), sm: theme.spacing(2.5), md: theme.spacing(3) },
                      '&:last-child': {
                        paddingBottom: { xs: theme.spacing(2), sm: theme.spacing(2.5), md: theme.spacing(3) },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: { xs: 2, sm: 2.5 },
                        width: '100%',
                      }}
                    >
                      <ProfileSection 
                        onClick={() => handleProfileClick(ther)}
                        sx={{
                          flex: { xs: '1 1 auto', sm: '1 1 0%' },
                          minWidth: { sm: '200px', md: '250px' },
                          maxWidth: { sm: '100%', md: '400px' },
                        }}
                      >
                        <Avatar
                          src={ther?.profilePicId ? getImageLink(ther?.profilePicId) : null}
                          sx={{
                            width: { xs: 48, sm: 56, md: 64 },
                            height: { xs: 48, sm: 56, md: 64 },
                            bgcolor: 'rgba(216, 101, 0, 0.1)',
                            border: '2px solid rgba(216, 101, 0, 0.2)',
                            flexShrink: 0,
                          }}
                        >
                          <Person sx={{ fontSize: { xs: 24, sm: 28, md: 32 } }} />
                        </Avatar>
                        <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                              color: 'text.primary',
                              marginBottom: 0.5,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              lineHeight: 1.3,
                            }}
                          >
                            {ther?.name ||
                              ther?.fullName ||
                              ther?.displayName ||
                              (isDoctors ? 'Unknown Doctor' : isScreener ? 'Unknown Screener' : 'Unknown Therapist')}
                          </Typography>
                          {/* Phase 14x — show approved therapy disciplines as small chips
                              below the practitioner's name when the parent surface opts in
                              (showDisciplineChips). Renders the human-readable label when
                              we know it ('psychotherapy' → 'Psychotherapy', 'nutrition-
                              consultation' → 'Nutrition Consultation'); otherwise falls
                              back to the raw slug. */}
                          {showDisciplineChips &&
                            Array.isArray(
                              ther?.teacherUnEditableFields?.approvedTherapyDisciplines,
                            ) &&
                            ther.teacherUnEditableFields.approvedTherapyDisciplines.length > 0 && (
                              <Stack
                                direction="row"
                                spacing={0.5}
                                useFlexGap
                                flexWrap="wrap"
                                sx={{ mt: 0.5 }}
                              >
                                {ther.teacherUnEditableFields.approvedTherapyDisciplines.map(
                                  (slug) => (
                                    <Chip
                                      key={`discipline-${slug}`}
                                      label={formatDisciplineLabel(slug)}
                                      size="small"
                                      sx={{
                                        height: '20px',
                                        fontSize: '0.6875rem',
                                        backgroundColor: 'rgba(216, 101, 0, 0.1)',
                                        color: 'rgba(216, 101, 0, 0.95)',
                                        fontWeight: 500,
                                      }}
                                    />
                                  ),
                                )}
                              </Stack>
                            )}
                          {ther?.entityFeeModels?.length > 0 && (
                            <Chip
                              label={`${ther.entityFeeModels.length} Engagement Model${ther.entityFeeModels.length > 1 ? 's' : ''}`}
                              size="small"
                              sx={{
                                height: '20px',
                                fontSize: '0.6875rem',
                                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                                color: 'rgba(16, 185, 129, 0.9)',
                                fontWeight: 500,
                                marginTop: 0.5,
                              }}
                            />
                          )}
                          {/* Enabled / disabled + KYC status — visible to every role that can
                              reach this listing, so the practitioner's state reads at a glance. */}
                          <Box sx={{ mt: 0.75 }}>
                            <PractitionerStatusChips
                              practitioner={ther}
                              practitionerLabel={selectedPractitionerLabel}
                            />
                          </Box>
                          {/* "All Screeners (by role)" — highlight ROLE_SCREENER source gaps
                              (User.java vs servicesApproved) and Screener Profile presence. */}
                          {showScreenerRoleSourceBadges && (
                            <ScreenerRoleSourceBadges practitioner={ther} />
                          )}
                          {/* Cumulative all-time totals — only for roles the practitioner-stats
                              admin endpoint admits (fetched in batch above). */}
                          {canViewCumulativeStats && (
                            <CumulativeStatsStrip stats={cumulativeStatsById?.[ther?.id]} />
                          )}
                        </Box>
                      </ProfileSection>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        {/* Clinic Assignments — view + edit which clinic locations this screener
                            is attached to. Shown to clinic roles (CSA / CSM / Admin). */}
                        {isScreener && ther?.id && canSeeClinicAssignments && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<BusinessIcon sx={{ fontSize: '14px' }} />}
                            onClick={() => setClinicAssignmentMentor(ther)}
                            sx={{
                              textTransform: 'none',
                              borderRadius: '8px',
                              whiteSpace: 'nowrap',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                            }}
                          >
                            Clinic Assignments
                          </Button>
                        )}
                        {/* Create / Edit Screener Profile — ROLE_SCREENER_ADMIN only, and only
                            enabled once servicesApproved.ROLE_SCREENER is on (the save endpoint
                            requires it). The tooltip explains the disabled state otherwise. */}
                        {canManageScreenerProfiles && ther?.hasPractitionerRecord && (
                          <Tooltip
                            arrow
                            title={
                              ther?.serviceApprovedScreenerEnabled
                                ? ''
                                : 'Enable servicesApproved.ROLE_SCREENER for this practitioner before a Screener Profile can be created.'
                            }
                          >
                            <span>
                              <Button
                                variant={ther?.hasScreenerProfile ? 'outlined' : 'contained'}
                                color={ther?.hasScreenerProfile ? 'primary' : 'success'}
                                size="small"
                                disabled={!ther?.serviceApprovedScreenerEnabled}
                                onClick={() => setScreenerProfileDialogMentor(ther)}
                                sx={{
                                  textTransform: 'none',
                                  borderRadius: '8px',
                                  whiteSpace: 'nowrap',
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                }}
                              >
                                {ther?.hasScreenerProfile
                                  ? 'Edit Screener Profile'
                                  : 'Create Screener Profile'}
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        {/* Cumulative "My Reports" insights across ALL clinics — the same shared
                            stats framework as the per-location Stats on the detail page. */}
                        {canViewCumulativeStats && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<TrendingUpIcon sx={{ fontSize: '14px' }} />}
                            onClick={() => setStatsDrawerMentor(ther)}
                            sx={{
                              textTransform: 'none',
                              borderRadius: '8px',
                              whiteSpace: 'nowrap',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              color: 'rgba(65, 133, 113, 0.95)',
                              borderColor: 'rgba(65, 133, 113, 0.5)',
                              '&:hover': {
                                borderColor: 'rgba(65, 133, 113, 0.95)',
                                backgroundColor: 'rgba(65, 133, 113, 0.06)',
                              },
                            }}
                          >
                            Stats
                          </Button>
                        )}
                        <PractitionerActionButtons
                          practitioner={ther}
                          variant={selectedMentorTargetRole}
                          excludeMenuItemsList={exculudeMenuItemsList}
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </TherapistCard>
              )
              })}
            {allTherapists?.length === 0 && !loadingTherapists && (
              <Box
                sx={{
                  textAlign: 'center',
                  padding: theme.spacing(8),
                  color: 'text.secondary',
                }}
              >
                <Typography variant="h6" sx={{ marginBottom: 1 }}>
                  {isScreener ? 'No screeners found' : 'No therapists found'}
                </Typography>
                <Typography variant="body2">
                  {isScreener
                    ? 'There are no screeners in this category at the moment.'
                    : 'There are no therapists available at the moment.'}
                </Typography>
              </Box>
            )}
          </Stack>
        )}
      </Container>

      {/* Profile Drawer */}
      <ProfileDrawer
        open={profileDrawerOpen}
        onClose={handleProfileDrawerClose}
        title={
          selectedMentor
            ? `${selectedMentor?.name || selectedMentor?.firstName || 'Practitioner'}${isScreener ? ' (Screener)' : ''} – Profile`
            : 'Profile'
        }
      >
        {selectedMentor?.id ? (
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'rgba(248, 250, 252, 0.9)' }}>
            <Stack spacing={1.5}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ sm: 'center' }}
                justifyContent="space-between"
              >
                <PractitionerStatusChips
                  practitioner={selectedPractitionerForStatus}
                  practitionerLabel={selectedPractitionerLabel}
                />
                <PractitionerEnableDisableControl
                  practitioner={selectedPractitionerForStatus}
                  practitionerType={selectedMentorTargetRole}
                  practitionerLabel={selectedPractitionerLabel}
                  onUpdated={handlePractitionerStatusUpdated}
                />
              </Stack>
              {canManagePatientContactVisibility && (
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                  }}
                >
                  <FormControlLabel
                    sx={{ m: 0, alignItems: 'flex-start', gap: 1 }}
                    control={
                      <Switch
                        size="small"
                        checked={showPatientContactDetailsOnMentorPortal}
                        onChange={handlePatientContactVisibilityChange}
                        disabled={loadingPatientContactVisibility || savingPatientContactVisibility}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                          Show Patient Contact Details on Mentor Portal
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          {loadingPatientContactVisibility
                            ? 'Loading current setting...'
                            : 'When off, patient email and mobile are not sent to this practitioner portal. Communication clients remain available.'}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              )}
              {canManagePatientScope && (
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                  }}
                >
                  <FormControlLabel
                    sx={{ m: 0, alignItems: 'flex-start', gap: 1 }}
                    control={
                      <Switch
                        size="small"
                        checked={restrictedToOwnPatients}
                        onChange={handlePatientScopeChange}
                        disabled={loadingPatientScope || savingPatientScope}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                          Restrict to own patients only
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          {loadingPatientScope
                            ? 'Loading current setting...'
                            : restrictedToOwnPatients
                            ? 'On: this practitioner sees only patients they have treated (my patients).'
                            : 'Off: this practitioner sees all patients of the clinic brand.'}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              )}
              {canManageTherapyReportReminder && (
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                  }}
                >
                  <FormControlLabel
                    sx={{ m: 0, alignItems: 'flex-start', gap: 1 }}
                    control={
                      <Switch
                        size="small"
                        checked={therapyReportReminderEnabled}
                        onChange={handleTherapyReportReminderChange}
                        disabled={
                          loadingCommunicationGates ||
                          Boolean(savingCommunicationGateKey) ||
                          Boolean(communicationGatesError) ||
                          !communicationGateSettings
                        }
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                          Therapy Report Completion Reminders
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          {loadingCommunicationGates
                            ? 'Loading current setting...'
                            : communicationGatesError
                              ? communicationGatesError
                              : savingCommunicationGateKey === COMMUNICATION_GATE_THERAPY_REPORT_COMPLETION_REMINDER
                                ? 'Saving reminder setting...'
                                : 'Sends WhatsApp and email only when pending reports or incomplete events could delay payouts.'}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              )}
              {canManagePayoutCycleCommunication && (
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Payout Cycle Communications
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    These switches opt this practitioner into the scheduled payout-cycle alerts. Global constants must also be enabled.
                  </Typography>
                  <Stack spacing={0.75}>
                    {PAYOUT_CYCLE_COMMUNICATION_GATE_ITEMS.map((item) => (
                      <FormControlLabel
                        key={item.key}
                        sx={{ m: 0, alignItems: 'flex-start', gap: 1 }}
                        control={
                          <Switch
                            size="small"
                            checked={Boolean(communicationGateValues[item.key])}
                            onChange={(event) =>
                              handleCommunicationGateChange(
                                item.key,
                                event.target.checked,
                                {
                                  enabled: `${item.label} enabled.`,
                                  disabled: `${item.label} disabled.`,
                                },
                              )
                            }
                            disabled={
                              loadingCommunicationGates ||
                              Boolean(savingCommunicationGateKey) ||
                              Boolean(communicationGatesError) ||
                              !communicationGateSettings
                            }
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                              {item.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                              {savingCommunicationGateKey === item.key ? 'Saving setting...' : item.description}
                            </Typography>
                          </Box>
                        }
                      />
                    ))}
                  </Stack>
                </Box>
              )}
              <SupportContactActions
                practitionerId={selectedMentor.id}
                targetRole={selectedMentorTargetRole}
                eventType={selectedMentorEventType}
                uiSurface="support-practitioner-profile-drawer"
                title="Practitioner Contact"
              />
            </Stack>
          </Box>
        ) : null}
        {loadingProfile && !screenerProfileData && !isScreener ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
            }}
          >
            <CircularProgress />
          </Box>
        ) : isScreener ? (
          <Box sx={{ pt: 0 }}>
            <Accordion defaultExpanded disableGutters sx={{ boxShadow: 'none', '&:before': { display: 'none' }, borderBottom: '1px solid', borderColor: 'divider' }}>
              <AccordionSummary expandIcon={<ExpandMore />} sx={{ fontWeight: 600 }}>
                Screener Profile
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {loadingProfile && !screenerProfileData ? (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={28} />
                  </Box>
                ) : (
                  <ScreenerProfileViewer profile={screenerProfileData} />
                )}
              </AccordionDetails>
            </Accordion>
            <Accordion defaultExpanded={false} disableGutters sx={{ boxShadow: 'none', '&:before': { display: 'none' }, borderBottom: '1px solid', borderColor: 'divider' }}>
              <AccordionSummary expandIcon={<ExpandMore />} sx={{ fontWeight: 600 }}>
                Mentor / Practitioner Profile
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {loadingProfile && !fullProfileData ? (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={28} />
                  </Box>
                ) : (
                  <ProfileViewer
                    profileData={fullProfileData || selectedMentor}
                    allTags={allTags}
                    onEdit={canEditPractitionerProfile ? handleEditPractitionerProfile : null}
                    readApiWithoutParams={getSPDataWithoutParams}
                    techniqueApiPathBuilder={(techniqueId) => `/api/secure/cc/support/technique/${techniqueId}`}
                    certificateListApiPathBuilder={(profileData) =>
                      `/api/secure/cc/support/practitioner/certificate-record/list/paginated?practitioner-id=${profileData?.id}&page=0&size=100`
                    }
                  />
                )}
              </AccordionDetails>
            </Accordion>
          </Box>
        ) : (
          <ProfileViewer
            profileData={fullProfileData || selectedMentor}
            allTags={allTags}
            onEdit={canEditPractitionerProfile ? handleEditPractitionerProfile : null}
            readApiWithoutParams={getSPDataWithoutParams}
            techniqueApiPathBuilder={(techniqueId) => `/api/secure/cc/support/technique/${techniqueId}`}
            certificateListApiPathBuilder={(profileData) =>
              `/api/secure/cc/support/practitioner/certificate-record/list/paginated?practitioner-id=${profileData?.id}&page=0&size=100`
            }
          />
        )}
      </ProfileDrawer>

      {canManageScreenerProfiles && (
        <ScreenerProfileCreateDialog
          open={Boolean(screenerProfileDialogMentor)}
          practitioner={screenerProfileDialogMentor}
          onClose={() => setScreenerProfileDialogMentor(null)}
          onSaved={handleScreenerProfileSaved}
        />
      )}

      {isScreener && (
        <ScreenerClinicAssignmentDialog
          open={Boolean(clinicAssignmentMentor)}
          practitioner={clinicAssignmentMentor}
          onClose={() => setClinicAssignmentMentor(null)}
        />
      )}

      {/* Across-all-clinics "My Reports" insights (no brand/location = cumulative scope). */}
      {canViewCumulativeStats && (
        <SupportPractitionerStatsDrawer
          open={Boolean(statsDrawerMentor)}
          practitioner={statsDrawerMentor}
          onClose={() => setStatsDrawerMentor(null)}
        />
      )}
    </>
  )
}

{
  /* <Button
sx={{ fontSize: '8px', minWidth: 'unset !important' }}
onClick={() => {
  navigate(ther?.slug)
  sessionStorage.setItem('supportMentorData', JSON.stringify(ther))
}}
variant="outlined"
size="small"
>
Clinics
</Button>
<Button
sx={{ fontSize: '8px', minWidth: 'unset !important' }}
onClick={() => {
  navigate(`${ther?.slug}/calendar`)
  sessionStorage.setItem('supportMentorData', JSON.stringify(ther))
}}
variant="outlined"
size="small"
>
Calendar
</Button>
<Button
sx={{ fontSize: '8px', minWidth: 'unset !important' }}
onClick={() => {
  navigate(`${ther?.slug}/events?tab=events`)
  sessionStorage.setItem('supportMentorData', JSON.stringify(ther))
}}
variant="outlined"
size="small"
>
Events
</Button>
<Button
sx={{ fontSize: '8px', minWidth: 'unset !important' }}
onClick={() => {
  navigate(`${ther?.slug}/engagement-models`)
  sessionStorage.setItem('supportMentorData', JSON.stringify(ther))
}}
disabled
variant="outlined"
size="small"
>
Engagement Models
</Button>
<Button
sx={{ fontSize: '8px', minWidth: 'unset !important' }}
onClick={() => {
  navigate(`${ther?.slug}/next-visits`)
  sessionStorage.setItem('supportMentorData', JSON.stringify(ther))
}}
disabled
variant="outlined"
size="small"
>
Scheduled Visits
</Button> */
}
